// <copyright file="record-delivery-dialog.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import { buttonsOkCancel, buttonsSaveCancel } from '../../../webmodule-common/other/ui/modal-confirmation';
import { checkValidations } from '../../../webmodule-common/other/ui/data-entry-screen-helpers';
import { DataBinding } from '../../../webmodule-common/other/ui/databinding/databinding';
import { DataTracker, FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { css, html, type TemplateResult } from 'lit';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { LangFabricationJobActions } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-actions-lang';
import { LitBaseModal } from '../../../webmodule-common/other/ui/modal/modal-factory-lit';
import { quoteItemCellStyles } from '../../../webmodule-common/other/ui/expandable-item-cell-styles';
import { WebModuleTable, WebModuleTableColumnDef } from '../../../webmodule-common/components/src/webmodule-components';

export interface DeliveryEntry {
  deliveryDate: string;
  notes: string;
}

/**
 * One selectable line in the dialog's checklist. Mirrors the quote items grid's columns so the
 * dialog shows the same information the user was just looking at - the caller renders options and
 * tags with the grid's own helpers and passes the result through.
 */
export interface DeliveryCandidate {
  quoteItemId: string;
  itemNumber?: string;
  thumbnailUrl?: string;
  location: string;
  description?: string;
  options?: string;
  tags?: TemplateResult;
  trackingReference?: string;
  quantity?: number;
}

export interface DeliveryDialogResult extends DeliveryEntry {
  /** The ticked items. Equals the caller's pre-resolved set when no checklist was shown. */
  quoteItemIds: string[];
}

/**
 * Injected into the <webmodule-table>'s shadow root via customStyles, where the page stylesheet
 * cannot reach the cell content. Reuses the item tables' cell vocabulary - quote-item-title stacks
 * the location over its description - and adds only the two things the checklist needs on top: the
 * thumbnail size, and the muted description colour that .text-muted would give in light DOM but
 * cannot cross the shadow boundary.
 */
const deliveryPickerStyles = css`
  ${quoteItemCellStyles}

  .delivery-thumb {
    width: 32px;
    height: 32px;
    object-fit: contain;
    display: block;
  }

  .quote-item-description {
    color: var(--gray-600);
  }
`;

/**
 * Collects the one date and one note that a Record Delivery action stamps onto every selected
 * quote item, and optionally the selection itself.
 *
 * Two callers, one intent. The job items table has already ticked its rows before opening this,
 * so it passes no candidates and the dialog is just the two fields. The quote items grid has no
 * row selection - delivery is triggered by clicking a single badge - so it passes every
 * Ready-for-Delivery item on the quote and the dialog renders a checklist, with the clicked one
 * pre-ticked.
 */
export class RecordDeliveryDialog extends LitBaseModal {
  public modalResult = false;
  private data: DeliveryEntry;
  private dataTracker: DataTracker;
  private forms: FormInputAssistant;
  private candidates: DeliveryCandidate[];
  private selected: Set<string>;
  private readonlyMode: boolean;

  constructor(
    initial: DeliveryEntry,
    candidates: DeliveryCandidate[] = [],
    preSelected: string[] = [],
    readonlyMode = false
  ) {
    super();
    this.data = { ...initial };
    this.candidates = candidates;
    this.selected = new Set(preSelected);
    this.readonlyMode = readonlyMode;
    const dataBinding = new DataBinding(
      this.ui,
      undefined,
      (input: string, internalId: string) => `recorddelivery-${input}-${internalId}`
    );
    this.dataTracker = new DataTracker(dataBinding);
    this.dataTracker.addObjectBinding(() => this.data, 'deliveryDate', 'deliveryDate', FieldType.date, true);
    this.dataTracker.addObjectBinding(() => this.data, 'notes', 'notes', FieldType.string, false);
    this.forms = new FormInputAssistant(this.dataTracker, readonlyMode);
  }

  /**
   * Without candidates the caller owns the selection and gets its own ids back untouched.
   * With candidates the dialog owns it and returns whatever was ticked.
   */
  public static async Execute(
    initial: DeliveryEntry,
    quoteItemIds: string[],
    candidates: DeliveryCandidate[] = []
  ): Promise<DeliveryDialogResult | null> {
    const dialog = new RecordDeliveryDialog(initial, candidates, quoteItemIds);
    await dialog.showModal();
    if (!dialog.modalResult) return null;
    return {
      ...dialog.data,
      quoteItemIds: candidates.length > 0 ? [...dialog.selected] : quoteItemIds
    };
  }

  /**
   * Shows a delivery that already happened: its items, date and notes, all read-only. Editing a
   * recorded delivery is not a requirement, so this deliberately offers no way to change one.
   */
  public static async View(entry: DeliveryEntry, items: DeliveryCandidate[]): Promise<void> {
    const dialog = new RecordDeliveryDialog(
      entry,
      items,
      items.map(i => i.quoteItemId),
      true
    );
    await dialog.showModal();
  }

  get isFooterVisible(): boolean {
    return true;
  }

  /** Only the checklist form needs the room; the job side is two fields. */
  protected get modalSize(): string {
    return this.candidates.length > 0 ? 'modal-xl' : 'modal-lg';
  }

  title(): string | TemplateResult {
    return this.readonlyMode
      ? LangFabricationJobActions.deliveryViewTitle()
      : LangFabricationJobActions.deliveryDialogTitle();
  }

  private toggle(quoteItemId: string, checked: boolean): void {
    if (checked) this.selected.add(quoteItemId);
    else this.selected.delete(quoteItemId);
    this.requestUpdate();
  }

  private candidateListTemplate(): TemplateResult {
    if (this.candidates.length === 0) return html``;
    return html`
      <div class="mb-3">
        ${this.readonlyMode
          ? html``
          : html`<div class="d-flex justify-content-end mb-1">
              <span class="text-muted"
                >${LangFabricationJobActions.deliverySelectedCount(this.selected.size, this.candidates.length)}</span
              >
            </div>`}
        <webmodule-table
          .customStyles=${deliveryPickerStyles}
          .columns=${this.candidateColumns()}
          .data=${this.candidates}
          .clickrows=${false}
          .keyevent=${(item: DeliveryCandidate) => item.quoteItemId}
          .rowidevent=${(item: DeliveryCandidate) => item.quoteItemId}
          .elevated=${false}
          .cellBorders=${false}
          .uppercaseHeader=${false}
          .bodyMaxHeight=${'22rem'}
          tablestyle="nestedtable"
        ></webmodule-table>
      </div>
    `;
  }

  /**
   * The checklist columns, mirroring the quote items grid so the dialog shows what the user was
   * just looking at. The webmodule-table owns the layout, sticky header and scroll (bodyMaxHeight);
   * the leading checkbox column is the dialog's own - it drives `this.selected`, the source of
   * truth the save reads - and is dropped entirely when only viewing a past delivery.
   */
  private candidateColumns(): WebModuleTableColumnDef[] {
    const columns: WebModuleTableColumnDef[] = [];

    if (!this.readonlyMode) {
      columns.push({
        fieldName: 'select',
        width: '2.75rem',
        align: 'center',
        title: () =>
          html`<webmodule-checkbox
            ?checked=${this.selected.size === this.candidates.length}
            title=${LangFabricationJobActions.deliverySelectAll()}
            @webmodule-change=${(e: Event) => {
              const on = (e.target as HTMLInputElement).checked;
              this.selected = new Set(on ? this.candidates.map(c => c.quoteItemId) : []);
              this.requestUpdate();
            }}
          ></webmodule-checkbox>`,
        displayValue: (_t: WebModuleTable, item: DeliveryCandidate) =>
          html`<webmodule-checkbox
            ?checked=${this.selected.has(item.quoteItemId)}
            @webmodule-change=${(e: Event) => this.toggle(item.quoteItemId, (e.target as HTMLInputElement).checked)}
          ></webmodule-checkbox>`
      });
    }

    columns.push(
      {
        fieldName: 'itemNumber',
        width: '4rem',
        title: () => LangFabricationJobActions.deliveryColItem(),
        displayValue: (_t: WebModuleTable, item: DeliveryCandidate) => item.itemNumber ?? ''
      },
      {
        fieldName: 'image',
        width: '4rem',
        title: () => LangFabricationJobActions.deliveryColImage(),
        displayValue: (_t: WebModuleTable, item: DeliveryCandidate) =>
          item.thumbnailUrl ? html`<img class="delivery-thumb" alt="" src=${item.thumbnailUrl} />` : html``
      },
      {
        fieldName: 'location',
        classes: 'quote-item-title',
        minWidth: '12rem',
        title: () => LangFabricationJobActions.deliveryColLocation(),
        displayValue: (_t: WebModuleTable, item: DeliveryCandidate) =>
          html`<span class="quote-item-title">${item.location}</span>
            <span class="quote-item-description">${item.description ?? ''}</span>`
      },
      {
        fieldName: 'options',
        minWidth: '8rem',
        title: () => LangFabricationJobActions.deliveryColOptions(),
        displayValue: (_t: WebModuleTable, item: DeliveryCandidate) => item.options ?? ''
      },
      {
        fieldName: 'tags',
        width: '6rem',
        title: () => LangFabricationJobActions.deliveryColTags(),
        displayValue: (_t: WebModuleTable, item: DeliveryCandidate) => item.tags ?? html``
      },
      {
        fieldName: 'trackingReference',
        width: '8rem',
        title: () => LangFabricationJobActions.deliveryColTracking(),
        displayValue: (_t: WebModuleTable, item: DeliveryCandidate) => item.trackingReference ?? ''
      },
      {
        fieldName: 'quantity',
        width: '4rem',
        align: 'right',
        title: () => LangFabricationJobActions.deliveryColQty(),
        displayValue: (_t: WebModuleTable, item: DeliveryCandidate) => item.quantity?.toString() ?? ''
      }
    );

    return columns;
  }

  bodyTemplate(): TemplateResult {
    return html`
      <form class="form-one-col">
        <div class="row">
          <div class="form-column">
            ${this.candidateListTemplate()}
            ${this.forms.date('deliveryDate', LangFabricationJobActions.deliveryDateLabel(), 'date')}
            ${this.forms.note('notes', LangFabricationJobActions.deliveryNotesLabel(), 1000)}
          </div>
        </div>
      </form>
    `;
  }

  footerTemplate(): TemplateResult | null | undefined {
    // Viewing a recorded delivery changes nothing, so the single button dismisses rather than
    // discards - "OK", not "Cancel".
    if (this.readonlyMode)
      return html`<webmodule-button size="small" variant="primary" @click=${() => this.hideModal()}>
        ${buttonsOkCancel().ok}
      </webmodule-button>`;
    const buttons = buttonsSaveCancel();
    return this.createConfirmCancelButtons(buttons.ok, () => this.saveAndClose(), buttons.cancel);
  }

  private async saveAndClose() {
    this.dataTracker.applyChangeToValue();
    const errors: string[] = [];
    if (isEmptyOrSpace(this.data.deliveryDate)) errors.push(LangFabricationJobActions.deliveryDateLabel());
    // Only the checklist form can end up with nothing selected; the job side arrives pre-resolved.
    if (this.candidates.length > 0 && this.selected.size === 0)
      errors.push(LangFabricationJobActions.deliveryNoItemsSelected());
    if (errors.length > 0) {
      await checkValidations(errors);
      return;
    }
    this.modalResult = true;
    await this.hideModal();
  }
}
